/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import { appConfig, appOptions } from 'actions/app.actions';
import coverImg from 'assets/login-cover-img.png';
import {
  AppLogo,
  CreateNewPassword,
  Legals,
  LoginForm,
  MfaCodeVerify,
  ResetPassword,
  SubscribeForm,
} from 'components';
import { SsoFailure } from 'components/SsoFailure';
import InvalidFirstLoginLink from 'components/InvalidFirstLoginLink';
import { appConfigSelector, appOptionsSelector } from 'reducers/app.reducer';
import { ROUTES } from '../../constants';
import {
  Brand,
  Content,
  ContentWrapper,
  Illustration,
  Wrapper,
  cssIllustration,
  cssLogo,
} from './Auth.style';

const Auth = (): JSX.Element => {
  const { programUrl } = useSelector(appConfigSelector);
  const { list } = useSelector(appOptionsSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!programUrl) {
      // the app store is reinitialised when user logs out but the auth page needs options
      // and config (which are first called when the whole app is mounted, not on auth page)
      // so we need to get those data again when the auth page is displayed after user logged out
      dispatch(appConfig.request());
      dispatch(appOptions.request());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const WEBFILES_BASE = `${programUrl}/web_files/ui/img`;
  const backgroundImgUri = list?.find(
    (item) => item.name === '$backgroundLogin',
  )?.value;

  return (
    <Wrapper>
      {/* in dev mode, the API returns '0' by default, */}
      {/* so check backgroundImgUri is a way to prevent from displaying nothing */}
      <Illustration>
        <img
          css={cssIllustration}
          src={
            programUrl && backgroundImgUri && backgroundImgUri !== '0'
              ? `${WEBFILES_BASE}/${backgroundImgUri}`
              : coverImg
          }
          alt="illustration"
        />
      </Illustration>
      <Content>
        <ContentWrapper>
          <Brand>
            <AppLogo login css={cssLogo} />
          </Brand>
          <Switch>
            <Route exact path={ROUTES.MFA_CODE_VERIFY}>
              <MfaCodeVerify />
            </Route>
            <Route exact path={ROUTES.LOGIN}>
              <LoginForm />
            </Route>
            {/* Le SubscribeForm gère lui-même la logique d'accès :
                - inscription activée sans token → formulaire vide
                - token présent → formulaire pré-rempli
                - inscription désactivée sans token → redirection vers login */}
            <Route exact path={ROUTES.SUBSCRIBE}>
              <SubscribeForm />
            </Route>
            <Route exact path={[ROUTES.FORGOT_PASSWORD, ROUTES.RESET_PASSWORD]}>
              <ResetPassword />
            </Route>
            <Route exact path={ROUTES.CREATE_NEW_PASSWORD}>
              <CreateNewPassword />
            </Route>
            <Route exact path={ROUTES.SSO_FAILURE}>
              <SsoFailure />
            </Route>
            <Route exact path={ROUTES.LINK_EXPIRED}>
              <InvalidFirstLoginLink />
            </Route>
            <Route>
              <Redirect to={ROUTES.LOGIN} />
            </Route>
          </Switch>
        </ContentWrapper>
        <Legals textProps={{ align: 'center', style: { fontSize: 14 } }} />
      </Content>
    </Wrapper>
  );
};
export default Auth;
